/**
 * Unify Questionnaire model — comprehensive FHIR schema.
 *
 * Questionnaires serve as templates for clinical notes, scored assessments
 * (PHQ-9, GAD-7), intake forms, and administrative documents.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Field                  │ Type            │ Description                    │
 * ├──────────────────────────────────────────────────────────────────────────┤
 * │ url                    │ uri             │ Canonical URL (template slug)  │
 * │ name                   │ string          │ Machine-friendly slug          │
 * │ title                  │ string          │ Human-readable display name    │
 * │ status                 │ code            │ active | draft | retired       │
 * │ subjectType            │ code[]          │ Typically ["Patient"]          │
 * │ version                │ string          │ Semantic version (e.g. 1.0.0)  │
 * │ publisher              │ string          │ Organization name              │
 * │ date                   │ date            │ Publication date (YYYY-MM-DD)  │
 * │ meta.tag[]             │ Coding[]        │ Category and flags             │
 * │ item[]                 │ Item[]          │ Questions/groups/display       │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * meta.tag systems:
 *  - TEMPLATE_CATEGORY_SYSTEM  → groups templates (appointments, measures, treatment-planning, forms, administrative; legacy: client-operations)
 *  - TEMPLATE_DEFAULT_TAG_SYSTEM → marks a template as default for its category
 *  - SCORED_MEASURE_DELIVERY_TAG → delivery method for scored measures
 *  - SCORED_MEASURE_SCORING_TAG  → scoring method for scored measures
 *
 * Custom extensions:
 *  - ITEM_CONTROL_EXT_URL (hl7.org) → rendering hints per item (drop-down, check-box, rich-text)
 *  - Scoring configuration (https://unify.health/fhir/StructureDefinition/scoring-configuration) → valueString JSON
 *  - Score range extensions (https://unify.health/fhir/score-range-*) → score interpretation ranges
 *
 * Template settings extensions (on root):
 *  - https://unify.health/fhir/template-settings → valueString JSON with display/behavior config
 */

import type { Questionnaire, Coding, Extension } from '@medplum/fhirtypes';
import {
  TEMPLATE_CATEGORY_SYSTEM,
  TEMPLATE_DEFAULT_TAG_SYSTEM,
  SCORED_MEASURE_DELIVERY_TAG,
  SCORED_MEASURE_SCORING_TAG,
  ITEM_CONTROL_EXT_URL,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
//  Coded value types
// ═══════════════════════════════════════════════════════════════════════════

/** Questionnaire.status values used by Unify. */
export type QuestionnaireStatus = 'active' | 'draft' | 'retired';

/** Template category codes applied via TEMPLATE_CATEGORY_SYSTEM tag. */
export type TemplateCategoryCode =
  | 'appointments'
  | 'measures'
  | 'treatment-planning'
  | 'forms'
  | 'administrative'
  | 'client-operations'
  | 'transfers';

/** Item control extension codes (from hl7.org itemControl). */
export type ItemControlCode =
  | 'drop-down'
  | 'check-box'
  | 'rich-text';

/** Questionnaire item.type values used by Unify. */
export type QuestionnaireItemType =
  | 'group'
  | 'display'
  | 'string'
  | 'text'
  | 'integer'
  | 'date'
  | 'choice';

// ═══════════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Get the template category code from meta.tag. */
export function getTemplateCategory(q: Questionnaire): TemplateCategoryCode | undefined {
  return q.meta?.tag?.find((t: Coding) => t.system === TEMPLATE_CATEGORY_SYSTEM)?.code as TemplateCategoryCode | undefined;
}

/** Check if this template is marked as the default for its category. */
export function isDefaultTemplate(q: Questionnaire): boolean {
  return q.meta?.tag?.some((t: Coding) => t.system === TEMPLATE_DEFAULT_TAG_SYSTEM) ?? false;
}

/** Get the scored measure delivery method tag. */
export function getScoredMeasureDelivery(q: Questionnaire): string | undefined {
  return q.meta?.tag?.find((t: Coding) => t.system === SCORED_MEASURE_DELIVERY_TAG)?.code;
}

/** Get the scored measure scoring method tag. */
export function getScoredMeasureScoring(q: Questionnaire): string | undefined {
  return q.meta?.tag?.find((t: Coding) => t.system === SCORED_MEASURE_SCORING_TAG)?.code;
}

/** Get the canonical URL (url field). */
export function getCanonicalUrl(q: Questionnaire): string | undefined {
  return q.url;
}

/** Get the item control extension code from a questionnaire item extension array. */
export function getItemControlCode(extensions: Extension[] | undefined): ItemControlCode | undefined {
  return extensions?.find((e) => e.url === ITEM_CONTROL_EXT_URL)
    ?.valueCodeableConcept?.coding?.[0]?.code as ItemControlCode | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Setter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Set or replace the template category tag on a Questionnaire. */
export function setTemplateCategory(q: Questionnaire, code: TemplateCategoryCode): void {
  if (!q.meta) q.meta = {};
  if (!q.meta.tag) q.meta.tag = [];
  const existing = q.meta.tag.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM);
  if (existing) {
    existing.code = code;
  } else {
    q.meta.tag.push({ system: TEMPLATE_CATEGORY_SYSTEM, code });
  }
}

/** Mark or unmark this template as the default for its category. */
export function setDefaultTemplate(q: Questionnaire, isDefault: boolean): void {
  if (!q.meta) q.meta = {};
  if (!q.meta.tag) q.meta.tag = [];
  q.meta.tag = q.meta.tag.filter((t) => t.system !== TEMPLATE_DEFAULT_TAG_SYSTEM);
  if (isDefault) {
    q.meta.tag.push({ system: TEMPLATE_DEFAULT_TAG_SYSTEM, code: 'true' });
  }
}
